import styled from '@emotion/styled';
import { useAuth } from '@teko/auth-react';
import {
  Body,
  Modal,
  ModalBody,
  ModalHeader,
  ModalProps,
} from '@teko/ds-mobile';
import { NavBar } from '@teko/teko-ecommerce';
import { ProfileUpdate } from '@teko/user';
import { setCookieOver18YearOld } from '@teko/utils';
import { useFeatureFlags } from 'contexts';
import { useTenantConfig } from 'hooks';
import { t } from 'i18n';
import { UpdateProfileForm } from 'modules/profile/shared/UpdateProfileForm';
import router, { useRouter } from 'next/router';
import React, { useState } from 'react';
import { CheckAgeModalContent } from './CheckAgeModalContent';

export interface ConfirmOver18YearOldModalProps {
  showPopupCheckAge: boolean;
  setShowPopupCheckAge: (value: boolean) => void;
  okText?: string;
  onCancel?: () => void;
}

export const ConfirmOver18YearOldModal: React.FC<
  ConfirmOver18YearOldModalProps
> = ({ showPopupCheckAge, setShowPopupCheckAge, okText, onCancel }) => {
  const { user } = useAuth();
  const { isMobile } = useTenantConfig();
  const { flags } = useFeatureFlags();

  const handleCancel = () => {
    onCancel ? onCancel() : router.push('/');
  };

  return (
    <React.Fragment>
      {showPopupCheckAge &&
        (flags.epic_ecom_315 && user ? (
          <UserRequireOver18Modal
            show={showPopupCheckAge}
            onCancel={handleCancel}
            onOk={() => {
              setShowPopupCheckAge(false);
            }}
          />
        ) : (
          <CheckAgeModalContent
            isMobile={isMobile}
            show={showPopupCheckAge}
            onCancel={handleCancel}
            onOk={() => {
              setCookieOver18YearOld(true);
              setShowPopupCheckAge(false);
            }}
            okText={okText}
          />
        ))}
    </React.Fragment>
  );
};

const UserRequireOver18Modal: React.FC<Omit<ModalProps, 'children'>> = ({
  show,
  onCancel,
}) => {
  const [showUpdateInfoForm, setShowUpdateInfoForm] = useState(false);
  const router = useRouter();
  const { query } = router;
  const { isMobile } = useTenantConfig();
  return (
    <>
      <Modal
        showCloseIcon={false}
        visible={show}
        size="s"
        cancelText={t('Quay lại')}
        onCancel={onCancel}
        okText={t('Cập nhật hồ sơ')}
        onOk={() => setShowUpdateInfoForm(true)}
      >
        <ModalHeader>
          {t('Thông báo')}
          <Body weight="regular" marginTop="s16">
            {t('Bạn phải ít nhất 18 tuổi để xem nội dung này')}
          </Body>
        </ModalHeader>
      </Modal>
      <UpdateProfiledModal
        className="update-profile-modal"
        visible={showUpdateInfoForm}
        onClose={() => setShowUpdateInfoForm(false)}
        footer={false}
        showCloseIcon={false}
        isMobile={isMobile}
      >
        {!isMobile ? (
          <StyledModalHeader>{t('Thông tin tài khoản')}</StyledModalHeader>
        ) : !query.hideHeader && !query.supportOmniAppUI ? (
          <NavBar
            title={t('Thông tin tài khoản')}
            goBack={() => setShowUpdateInfoForm(false)}
          />
        ) : null}
        <StyledModalBody className="modal-body">
          <UpdateProfileForm
            isMobile={isMobile}
            transforms={{
              uploadProfile: (defaultBehavior) => {
                return async (profile: ProfileUpdate) => {
                  const res = await defaultBehavior(profile);
                  router.reload();
                  return res;
                };
              },
            }}
          />
        </StyledModalBody>
      </UpdateProfiledModal>
    </>
  );
};

const UpdateProfiledModal = styled(Modal)<{ isMobile?: boolean }>(
  ({ isMobile }) => `
  border-radius: 5px;
  max-width: 32rem;
  ${
    isMobile
      ? `
      .modal-body{
        padding: 0.75rem;
      }
      .teko-modal-content {
        height: 100vh;
        max-height: 900px;
        border-radius: 0;
        padding: 0;
      }
        `
      : ''
  }
`,
);

const StyledModalHeader = styled(ModalHeader)`
  text-align: left;
  padding-bottom: 0.5rem;
`;

const StyledModalBody = styled(ModalBody)`
  max-height: 80vh;
  overflow-y: auto;
`;
